@if (slides$ | async; as slides) {
  @if (slides[currentSlideIndex()]; as currentSlide) {
    <!-- Mobile & Tablet Layout -->
    @if (isMobile() || isTablet()) {
      <div class="mobile-layout fixed inset-0 pt-14 pb-16 flex flex-col bg-gray-50 print:hidden">
        <!-- Mobile Slide Viewer -->
        <div class="mobile-slide-section shrink-0 border-b border-gray-200 flex flex-col">
          <!-- Navigation Header -->
          <div class="flex items-center justify-between px-3 py-2 border-b border-gray-100 shrink-0 bg-white">
            <button
              (click)="prevSlide()"
              [disabled]="currentSlideIndex() === 0"
              class="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors cursor-pointer"
            >
              <lucide-icon [img]="icons.ChevronLeft" class="w-5 h-5"></lucide-icon>
            </button>
            <div class="flex flex-col items-center">
              <span class="text-sm font-semibold text-gray-700">
                Slide {{ getSlideNumber() }} / {{ slides.length }}
              </span>
              <span class="text-xs text-brand font-medium">{{ getCurrentPhase() }}</span>
            </div>
            <button
              (click)="nextSlide()"
              [disabled]="currentSlideIndex() === slides.length - 1"
              class="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors cursor-pointer"
            >
              <lucide-icon [img]="icons.ChevronRight" class="w-5 h-5"></lucide-icon>
            </button>
          </div>

          <!-- Slide Preview (simplified, contained) -->
          <div class="mobile-slide-container flex-1 min-h-0 p-3 flex items-center justify-center overflow-hidden bg-gray-100">
            @if ((isFetchingThumbnails$ | async)) {
              <div class="flex flex-col justify-center items-center bg-white rounded-lg shadow-md p-8">
                <div class="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin mb-2"></div>
                <p class="text-xs text-gray-500">Loading...</p>
              </div>
            } @else if (getThumbnailForSlide(currentSlideIndex()); as thumbnail) {
              <img 
                [src]="thumbnail.thumbnailUrl" 
                alt="Slide {{ getSlideNumber() }}"
                class="max-w-full max-h-full object-contain rounded-lg shadow-md"
                loading="lazy"
              />
            } @else {
              <div class="flex items-center justify-center bg-white rounded-lg shadow-md p-8">
                <span class="text-sm text-gray-400">No preview</span>
              </div>
            }
          </div>
        </div>

        <!-- Mobile Tabs -->
        <div class="mobile-tabs shrink-0 bg-white border-b border-gray-200">
          <div class="flex">
            <button
              (click)="setMobileTab('lesson-plan')"
              class="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors cursor-pointer"
              [class.text-brand]="mobileActiveTab() === 'lesson-plan'"
              [class.border-b-2]="mobileActiveTab() === 'lesson-plan'"
              [class.border-brand]="mobileActiveTab() === 'lesson-plan'"
              [class.text-gray-500]="mobileActiveTab() !== 'lesson-plan'"
            >
              <lucide-icon [img]="icons.Map" class="w-4 h-4"></lucide-icon>
              Lesson Plan
            </button>
            <button
              (click)="setMobileTab('teacher-script')"
              class="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors cursor-pointer"
              [class.text-brand]="mobileActiveTab() === 'teacher-script'"
              [class.border-b-2]="mobileActiveTab() === 'teacher-script'"
              [class.border-brand]="mobileActiveTab() === 'teacher-script'"
              [class.text-gray-500]="mobileActiveTab() !== 'teacher-script'"
            >
              <lucide-icon [img]="icons.FileText" class="w-4 h-4"></lucide-icon>
              Teacher's Script
            </button>
          </div>
        </div>

        <!-- Mobile Tab Content -->
        <div class="mobile-tab-content flex-1 overflow-y-auto">
          @if (mobileActiveTab() === 'lesson-plan') {
            <!-- Lesson Plan Content -->
            @if (planStructure$ | async; as plan) {
              <app-lesson-map
                [plan]="plan"
                [slides]="slides"
                [activeBlockId]="activeBlockId()"
                [activeSlideIndex]="currentSlideIndex()"
                [thumbnails]="(thumbnails$ | async) ?? []"
                [mobileMode]="true"
                (slideSelected)="onSlideSelected($event)"
                (editRequested)="openEditRequestModal()"
              ></app-lesson-map>
            }
          } @else {
            <!-- Teacher's Script Content -->
            <div class="p-4 space-y-3">
              @for (note of getSlideNotes(currentSlide); track $index) {
                <div
                  class="p-4 rounded-xl border-l-4"
                  [class.bg-blue-50]="note.isAction"
                  [class.border-blue-400]="note.isAction"
                  [class.bg-gray-50]="!note.isAction"
                  [class.border-brand]="!note.isAction"
                >
                  <div class="text-sm leading-relaxed text-gray-700">{{ note.text }}</div>
                </div>
              }
              @if (getSlideNotes(currentSlide).length === 0) {
                <div class="text-center py-8 text-gray-400">
                  <p class="text-sm">No speaker notes for this slide</p>
                </div>
              }
            </div>
          }
        </div>
      </div>
    } @else {
      <!-- Desktop/Tablet Layout -->
      <div class="presenter-layout fixed inset-0 pt-16 flex print:hidden">
        <!-- Left: Lesson Map Sidebar -->
        @if (planStructure$ | async; as plan) {
          <div class="shrink-0 hidden lg:block">
            <app-lesson-map
              [plan]="plan"
              [slides]="slides"
              [activeBlockId]="activeBlockId()"
              [activeSlideIndex]="currentSlideIndex()"
              [thumbnails]="(thumbnails$ | async) ?? []"
              (slideSelected)="onSlideSelected($event)"
              (editRequested)="openEditRequestModal()"
            ></app-lesson-map>
          </div>
        }

        <!-- Middle: Slide Canvas Area (Google Slides style) -->
        <div class="flex-1 flex flex-col bg-gray-100 min-w-0 overflow-hidden">
          <!-- Navigation Toolbar -->
          <div class="shrink-0 h-10 bg-white border-b border-gray-200 flex items-center px-3 gap-2">
            <!-- Slide Navigation -->
            <div class="flex items-center gap-1">
              <button
                (click)="prevSlide()"
                [disabled]="currentSlideIndex() === 0"
                class="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors cursor-pointer"
                title="Previous slide"
              >
                <lucide-icon [img]="icons.ChevronLeft" class="w-4 h-4"></lucide-icon>
              </button>
              <span class="text-sm font-medium text-gray-600 min-w-[60px] text-center">
                {{ getSlideNumber() }} / {{ slides.length }}
              </span>
              <button
                (click)="nextSlide()"
                [disabled]="currentSlideIndex() === slides.length - 1"
                class="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors cursor-pointer"
                title="Next slide"
              >
                <lucide-icon [img]="icons.ChevronRight" class="w-4 h-4"></lucide-icon>
              </button>
            </div>

            <div class="flex-1"></div>

            <!-- Phase indicator -->
            <div class="flex items-center gap-2 text-xs">
              <span class="text-gray-500 font-medium">Phase:</span>
              <span class="text-brand font-semibold">{{ getCurrentPhase() }}</span>
            </div>
          </div>

          <!-- Slide Canvas Component -->
          <div class="flex-1 overflow-hidden">
            <app-slide-canvas
              [slide]="currentSlide"
              [slideNumber]="getSlideNumber()"
              [totalSlides]="slides.length"
              [thumbnail]="getThumbnailForSlide(currentSlideIndex())"
              [isLoadingThumbnail]="(isFetchingThumbnails$ | async) ?? false"
              [presentationUrl]="(googleSlides$ | async)?.presentationUrl ?? null"
              [simplifiedMode]="isTablet()"
              (leaveFeedback)="openFeedbackModal()"
            ></app-slide-canvas>
          </div>
        </div>

        <!-- Right: Speaker Notes Panel (hidden on tablet) -->
        <div class="shrink-0 w-[260px] bg-white border-l border-gray-200 flex flex-col overflow-hidden hidden lg:flex">
          <!-- Notes Header -->
          <div class="shrink-0 border-b border-gray-100 px-4 py-3 flex items-center justify-between bg-gray-50">
            <span class="bg-brand/10 text-brand px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              Speaker Notes
            </span>
          </div>
          
          <!-- Notes Content -->
          <div class="flex-1 overflow-y-auto p-4 custom-scrollbar">
            <div class="space-y-4">
              @for (note of getSlideNotes(currentSlide); track $index) {
                <div
                  class="p-4 rounded-xl border-l-4"
                  [class.bg-blue-50]="note.isAction"
                  [class.border-blue-400]="note.isAction"
                  [class.bg-gray-50]="!note.isAction"
                  [class.border-brand]="!note.isAction"
                >
                  <div class="text-sm leading-relaxed text-gray-700">{{ note.text }}</div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Feedback Modal -->
    <app-feedback-modal 
      [isOpen]="isFeedbackModalOpen()" 
      [feedbackType]="'leave_feedback'"
      [lessonPlanId]="lessonPlanService.lessonPlanId"
      (closed)="closeFeedbackModal()"
    ></app-feedback-modal>

    <!-- Edit Request Feedback Modal -->
    <app-feedback-modal 
      [isOpen]="isEditRequestModalOpen()" 
      [feedbackType]="'edit_request'"
      [lessonPlanId]="lessonPlanService.lessonPlanId"
      (closed)="closeEditRequestModal()"
    ></app-feedback-modal>
  }
}
